import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ARCH_SURFACES,
  COMMON_SURFACE_COMBINATIONS,
  QUADRANT_SURFACES,
  SEXTANT_SURFACES,
  SINGLE_SURFACES,
} from "../../utils/surfaceSelectionConstants";
import { toggleSelectionValue } from "../../utils/selectionValueUtils";
import {
  clearAreaSurfaces,
  keepOnlyAreaSurfaces,
  keepOnlyMatchingAreaFamily,
} from "../../utils/toothSurfacesUtils";
import type { SurfaceSelectionTab } from "../../utils/treatAreaCapabilities";
import { ALL_SURFACE_TABS } from "../../utils/treatAreaCapabilities";
import {
  SelectionModalTabs,
  SelectionModalTab,
  SelectionModalSection,
  SelectionSectionHeader,
  SelectionSectionHeaderTitle,
  SelectionModalSectionBody,
  SelectionOptionButton,
  CustomInputRow,
  CustomInput,
  CustomAddButton,
  SurfaceOptionsRow,
  QuadGrid,
  QuadOptionButton,
  QuadOptionCode,
  QuadOptionLabel,
  ArchOptionsRow,
  ArchOptionButton,
} from "../SelectionModal/SelectionModal.styled";

const DisabledTabTooltipTarget = styled("span")({
  flex: 1,
  display: "flex",
});

interface SurfaceSelectionPanelProps {
  value: string[];
  onChange: (
    value: string[],
    meta?: { clearTeeth?: boolean },
  ) => void;
  /** When empty, all tabs are allowed (legacy). */
  allowedTabs?: SurfaceSelectionTab[];
  initialTab?: SurfaceSelectionTab;
}

const SurfaceSelectionPanel: React.FC<SurfaceSelectionPanelProps> = ({
  value,
  onChange,
  allowedTabs,
  initialTab,
}) => {
  const { t } = useTranslation();
  const resolvedAllowedTabs =
    allowedTabs && allowedTabs.length > 0 ? allowedTabs : ALL_SURFACE_TABS;
  const defaultTab =
    initialTab && resolvedAllowedTabs.includes(initialTab)
      ? initialTab
      : resolvedAllowedTabs[0];

  const [activeTab, setActiveTab] = useState<SurfaceSelectionTab>(defaultTab);
  const [customValue, setCustomValue] = useState("");

  useEffect(() => {
    if (!resolvedAllowedTabs.includes(activeTab)) {
      setActiveTab(defaultTab);
    }
  }, [activeTab, defaultTab, resolvedAllowedTabs]);

  const cannotSelectReason = t("treatmentPlan.cannotSelectWithTheseCodes");

  const isTabAllowed = (tab: SurfaceSelectionTab) =>
    resolvedAllowedTabs.includes(tab);

  const renderTab = (tab: SurfaceSelectionTab, label: string) => {
    const allowed = isTabAllowed(tab);
    const tabButton = (
      <SelectionModalTab
        type="button"
        active={activeTab === tab}
        disabled={!allowed}
        onClick={() => {
          if (!allowed) return;
          handleTabChange(tab);
        }}
      >
        {label}
      </SelectionModalTab>
    );

    if (allowed) {
      return tabButton;
    }

    return (
      <Tooltip title={cannotSelectReason} arrow placement="top">
        <DisabledTabTooltipTarget>{tabButton}</DisabledTabTooltipTarget>
      </Tooltip>
    );
  };

  const handleTabChange = (tab: SurfaceSelectionTab) => {
    setActiveTab(tab);
    if (tab === "surface") {
      onChange(clearAreaSurfaces(value), { clearTeeth: false });
      return;
    }
    // Keep current area values on tab switch; XOR clear happens on select.
    onChange(keepOnlyAreaSurfaces(value), { clearTeeth: true });
  };

  const handleToggleToothSurface = (surface: string) => {
    const base = clearAreaSurfaces(value);
    onChange(
      toggleSelectionValue(base, surface, (item) => item.toUpperCase()),
      { clearTeeth: false },
    );
  };

  const handleToggleAreaSurface = (surface: string) => {
    const base = keepOnlyMatchingAreaFamily(value, surface);
    onChange(
      toggleSelectionValue(base, surface, (item) => item.toUpperCase()),
      { clearTeeth: true },
    );
  };

  const handleCustomAdd = () => {
    if (!customValue.trim()) return;
    const base = clearAreaSurfaces(value);
    onChange(
      toggleSelectionValue(base, customValue, (item) => item.toUpperCase()),
      { clearTeeth: false },
    );
    setCustomValue("");
  };

  return (
    <>
      <SelectionModalTabs>
        {renderTab("surface", t("treatmentPlan.surfaceTabSurface"))}
        {renderTab("quad", t("treatmentPlan.surfaceTabQuad"))}
        {renderTab("sextant", t("treatmentPlan.surfaceTabSextant"))}
        {renderTab("arch", t("treatmentPlan.surfaceTabArch"))}
      </SelectionModalTabs>

      {activeTab === "surface" && isTabAllowed("surface") && (
        <>
          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.singleSurface")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
              <SurfaceOptionsRow>
                {SINGLE_SURFACES.map((surface) => (
                  <SelectionOptionButton
                    key={surface}
                    type="button"
                    selected={value.includes(surface)}
                    onClick={() => handleToggleToothSurface(surface)}
                  >
                    {surface}
                  </SelectionOptionButton>
                ))}
              </SurfaceOptionsRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.commonCombinations")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
              <SurfaceOptionsRow>
                {COMMON_SURFACE_COMBINATIONS.map((surface) => (
                  <SelectionOptionButton
                    key={surface}
                    type="button"
                    selected={value.includes(surface)}
                    onClick={() => handleToggleToothSurface(surface)}
                  >
                    {surface}
                  </SelectionOptionButton>
                ))}
              </SurfaceOptionsRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>

          <SelectionModalSection>
            <SelectionSectionHeader>
              <SelectionSectionHeaderTitle>
                {t("treatmentPlan.customSurface")}
              </SelectionSectionHeaderTitle>
            </SelectionSectionHeader>
            <SelectionModalSectionBody>
              <CustomInputRow>
                <CustomInput
                  value={customValue}
                  placeholder={t("treatmentPlan.enterPlaceholder")}
                  onChange={(event) => setCustomValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCustomAdd();
                    }
                  }}
                />
                <CustomAddButton type="button" onClick={handleCustomAdd}>
                  {t("treatmentPlan.add")}
                </CustomAddButton>
              </CustomInputRow>
            </SelectionModalSectionBody>
          </SelectionModalSection>
        </>
      )}

      {activeTab === "quad" && isTabAllowed("quad") && (
        <SelectionModalSectionBody>
          <QuadGrid>
            {QUADRANT_SURFACES.map((quad) => (
              <QuadOptionButton
                key={quad.code}
                type="button"
                selected={value.includes(quad.code)}
                onClick={() => handleToggleAreaSurface(quad.code)}
              >
                <QuadOptionCode>{quad.code}</QuadOptionCode>
                <QuadOptionLabel>{quad.label}</QuadOptionLabel>
              </QuadOptionButton>
            ))}
          </QuadGrid>
        </SelectionModalSectionBody>
      )}

      {activeTab === "sextant" && isTabAllowed("sextant") && (
        <SelectionModalSectionBody>
          <SurfaceOptionsRow>
            {SEXTANT_SURFACES.map((sextant) => (
              <SelectionOptionButton
                key={sextant}
                type="button"
                size="md"
                selected={value.includes(sextant)}
                onClick={() => handleToggleAreaSurface(sextant)}
              >
                {sextant}
              </SelectionOptionButton>
            ))}
          </SurfaceOptionsRow>
        </SelectionModalSectionBody>
      )}

      {activeTab === "arch" && isTabAllowed("arch") && (
        <SelectionModalSectionBody>
          <ArchOptionsRow>
            {ARCH_SURFACES.map((arch) => (
              <ArchOptionButton
                key={arch.code}
                type="button"
                selected={value.includes(arch.code)}
                onClick={() => handleToggleAreaSurface(arch.code)}
              >
                <QuadOptionCode>{arch.code}</QuadOptionCode>
                <QuadOptionLabel>{arch.label}</QuadOptionLabel>
              </ArchOptionButton>
            ))}
          </ArchOptionsRow>
        </SelectionModalSectionBody>
      )}
    </>
  );
};

export default SurfaceSelectionPanel;
